<template>
  <Modal
    :show="summaryModal.isOpen"
    fullscreen
    hide-default-actions
    content-class="summary-picker-modal"
    @close="closeSummaryModal"
  >
    <template #title>
      <div class="text-h2 text-textDarkest">
        Choose Job Summary
        <div class="text-body-1 text-textDark mt-2">
          Choose a summary below to append or replace.
          <a
            href="https://help.datapeople.io/article/370-contextual-job-summary"
            target="_blank"
            rel="noopener noreferrer"
            class="text-linkDefault"
          >
            Learn more about summary generation options.
          </a>
        </div>
      </div>
    </template>

    <template #content>
      <div class="h-100">
        <v-sheet
          max-width="1200"
          class="mx-auto d-flex flex-column ga-4 bg-transparent pb-6"
        >
          <div
            class="bg-bgDefault border border-strokeDefault border-opacity-100 rounded-lg pa-3 d-flex flex-column ga-2"
          >
            <div class="text-body-2 text-textDarkest">
              Current Job Summary
            </div>
            <div
              v-if="currentSummaryText"
              class="d-flex align-center ga-4"
            >
              <div
                class="text-body-3 text-textDark flex-grow-1"
                :class="isCurrentExpanded ? 'expanded' : 'collapsed'"
              >
                {{ currentSummaryText }}
              </div>
              <v-sheet
                min-width="80"
                class="mb-auto"
              >
                <a
                  class="text-body-3 text-linkDefault text-no-wrap cursor-pointer"
                  @click="isCurrentExpanded = !isCurrentExpanded"
                >
                  {{ isCurrentExpanded ? 'Show less' : 'View full text' }}
                </a>
              </v-sheet>
            </div>
            <div
              v-else
              class="text-body-3 text-textLight"
            >
              No current summary.
            </div>
          </div>

          <v-row>
            <v-col
              v-for="card in CARDS"
              :key="card.key"
              cols="6"
              class="d-flex"
            >
              <SummaryOptionCard
                :title="card.title"
                :subtitle="card.subtitle"
                :option="summaryModal.options[card.key]"
                :selected="summaryModal.selected === card.key"
                @select="selectSummaryOption(card.key)"
                @append="appendSummaryContent(card.key)"
                @replace="applySummaryContent(card.key)"
                @retry="retrySummaryOption(card.key)"
              />
            </v-col>
          </v-row>
        </v-sheet>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useContextualProfileSummary } from '@/stores/jobProfiles/contextualProfileSummary';
import { htmlToText } from '@ui/helpers/htmlToText';
import Modal from '@/components/shared/Modal.vue';
import SummaryOptionCard from './SummaryOptionCard.vue';

const CARDS = [
  { key: 'default', title: 'Base' },
  { key: 'more_specific', title: 'Contextual', subtitle: 'from your top matching external job posts' },
  { key: 'more_junior', title: 'Junior' },
  { key: 'more_senior', title: 'Senior' },
];

const {
  summaryModal,
  closeSummaryModal,
  selectSummaryOption,
  applySummaryContent,
  appendSummaryContent,
  retrySummaryOption,
} = useContextualProfileSummary();

const { editorContents, summaryFieldId } = storeToRefs(useCurrentJobProfile());

const isCurrentExpanded = ref(false);

const currentSummaryText = computed(() => {
  const id = summaryFieldId.value;
  if (!id) return '';
  const field = editorContents.value?.[id] || {};
  const html = [field.header, field.content, field.footer].filter(Boolean).join(' ');
  return htmlToText(html).trim();
});

watch(() => summaryModal.isOpen, (isOpen) => {
  if (isOpen) isCurrentExpanded.value = false;
});
</script>

<style lang="less">
.summary-picker-modal {
  & > .v-sheet {
    background-color: rgb(var(--v-theme-bgCanvas));
  }
}
</style>

<style scoped lang="less">
.collapsed {
  overflow: hidden;
  height: 36px;
}

.expanded {
  min-height: 54px; // 3 lines of text-body-3 (12px * 1.5)
}
</style>
